import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/Header-blue.svg';
import api from '../utils/api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: 'Master Task & Case Management',
      description:
        'Efficiently organize your mediations, assign tasks, set deadlines, and track progress.',
    },
    {
      title: 'Streamline Your Workflow',
      description:
        'Manage cases, inquiries, billing, and scheduling all from one unified platform.',
    },
    {
      title: 'Data-Driven Insights',
      description:
        'Track mediator performance, case trends, and financial metrics with real-time analytics.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reset link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen max-h-screen w-full flex flex-col md:flex-row bg-white overflow-hidden">
      {/* Left Panel - Form Area */}
      <div className="w-full md:w-1/2 h-screen max-h-screen flex flex-col p-6 md:p-8 lg:p-10 bg-white overflow-hidden shrink-0">
        {/* Logo */}
        <div className="mb-4 shrink-0">
          <img src={logo} alt="C.A.R. Dispute Resolution Center" className="h-12 object-contain" />
        </div>

        {/* Centered Content */}
        <div className="flex-grow min-h-0 flex flex-col justify-center max-w-md w-full mx-auto py-4">
          {/* Back Link */}
          <Link
            to="/login"
            className="group mb-8 flex items-center text-[#5b5a64] hover:text-[#0e1e20] transition-colors"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 group-hover:-translate-x-1 transition-transform"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span className="text-sm font-medium">Back to Sign In</span>
          </Link>

          {!submitted ? (
            <>
              <div className="mb-8">
                <h1
                  className="text-[30px] font-bold text-[#0e1e20] mb-2"
                  style={{ fontFamily: "'Inter Tight', sans-serif" }}
                >
                  Reset Password
                </h1>
                <p
                  className="text-[#5b5a64] text-base"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Enter your email address to receive a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-[#0e1e20] mb-1.5"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@scalemediation.com"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#e5e5e5] text-sm text-[#0e1e20] placeholder-[#9ca3af] outline-none transition-all focus:border-[#115e59] focus:ring-2 focus:ring-[#115e59]/20"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                {/* Send Reset Link Button */}
                <button
                  type="submit"
                  className="w-full bg-[#115e59] text-white py-3 rounded-lg font-medium hover:bg-[#0f766e] active:bg-[#134e4a] transition-colors text-base"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Send Reset Link
                </button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center">
              {/* Green Checkmark */}
              <div className="w-16 h-16 bg-[#d1fae5] rounded-full flex items-center justify-center mx-auto mb-5">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#065f46"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2
                className="text-2xl font-bold text-[#0e1e20] mb-2"
                style={{ fontFamily: "'Inter Tight', sans-serif" }}
              >
                Check Your Email
              </h2>
              <p
                className="text-[#5b5a64] text-base mb-2"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                We've sent a password reset link to{' '}
                <span className="font-medium text-[#0e1e20]">{email}</span>.
              </p>
              <p
                className="text-[#5b5a64] text-sm mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Please check your inbox and click the link to reset your password.
              </p>
              <p
                className="text-sm text-[#5b5a64]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Didn't receive the email?{' '}
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-[#115e59] font-medium hover:text-[#0f766e] transition-colors"
                >
                  Try again
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="mt-4 text-xs text-[#5b5a64] shrink-0"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          &copy; 2026 Scale Mediation
        </div>
      </div>

      {/* Right Panel - Teal Visual Area */}
      <div className="hidden md:flex w-1/2 h-screen max-h-screen relative overflow-hidden flex-col items-center justify-center p-6 lg:p-8 text-center shrink-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#115e59] via-[#0d5450] to-[#0d4f4a]" />

        {/* Dashboard Preview Card */}
        <div className="relative z-10 w-full max-w-[560px] mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 ring-1 ring-white/20 shadow-2xl">
            <div className="bg-white rounded-xl p-5 space-y-4">
              {/* Mock Header */}
              <div className="flex items-center justify-between">
                <img src={logo} alt="" className="h-7 object-contain" />
                <div className="flex gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#f1f1f5]" />
                  <div className="w-7 h-7 rounded-lg bg-[#f1f1f5]" />
                </div>
              </div>

              {/* KPI Cards Row */}
              <div className="grid grid-cols-4 gap-2.5">
                {[
                  { label: 'Active Cases', value: '127', color: '#115e59' },
                  { label: 'Pending', value: '34', color: '#0f766e' },
                  { label: 'Resolved', value: '89', color: '#14b8a6' },
                  { label: 'Revenue', value: '$42K', color: '#5b5a64' },
                ].map((kpi, i) => (
                  <div
                    key={i}
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: '#f8fffe' }}
                  >
                    <div
                      className="text-[10px] text-[#5b5a64] mb-1 truncate"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {kpi.label}
                    </div>
                    <div
                      className="text-sm font-bold"
                      style={{ color: kpi.color, fontFamily: "'Inter Tight', sans-serif" }}
                    >
                      {kpi.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart Placeholder */}
              <div className="bg-[#f8fffe] rounded-lg p-3">
                <div
                  className="text-[10px] text-[#5b5a64] mb-2"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Monthly Cases
                </div>
                <div className="flex items-end gap-1.5 h-16">
                  {[40, 65, 50, 80, 60, 90, 75, 85, 55, 70, 95, 68].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-sm transition-all"
                      style={{
                        height: `${h}%`,
                        backgroundColor: i === 10 ? '#115e59' : '#a7d8d4',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Table Rows */}
              <div className="space-y-1.5">
                {[1, 2, 3].map((row) => (
                  <div key={row} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-[#f8fffe]">
                    <div className="w-5 h-5 rounded-full bg-[#a7d8d4]" />
                    <div className="flex-1">
                      <div className="h-2 bg-[#e5e5e5] rounded-full w-3/4" />
                    </div>
                    <div className="h-2 bg-[#e5e5e5] rounded-full w-12" />
                    <div
                      className="text-[8px] px-1.5 py-0.5 rounded-full font-medium"
                      style={{
                        backgroundColor: row === 1 ? '#d1fae5' : row === 2 ? '#fef3c7' : '#dbeafe',
                        color: row === 1 ? '#065f46' : row === 2 ? '#92400e' : '#1e40af',
                      }}
                    >
                      {row === 1 ? 'Active' : row === 2 ? 'Pending' : 'Closed'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Text Carousel */}
        <div className="relative z-20 max-w-lg mx-auto">
          <div className="transition-all duration-500 ease-in-out">
            <h2
              className="text-2xl font-bold text-white mb-3 tracking-tight"
              style={{ fontFamily: "'Inter Tight', sans-serif" }}
            >
              {slides[currentSlide].title}
            </h2>
            <p
              className="text-white/80 text-base leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {slides[currentSlide].description}
            </p>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-3 mt-6">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentSlide
                    ? 'w-12 bg-white'
                    : 'w-12 bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
